package com.evoapps.evoshift.ui.growth

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ContentCopy
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalClipboard
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.ui.Cream
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.adaptive.FormMaxWidth
import com.evoapps.evoshift.ui.adaptive.contentWidth
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.util.plainTextClipEntry
import com.evoapps.evoshift.viewmodels.RecommendUiState
import com.evoapps.evoshift.viewmodels.RecommendViewModel
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.EvoTopBar
import com.evoapps.evoshift.widgets.GoldButton
import com.evoapps.evoshift.widgets.RegularCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.error_state_retry
import evoshift.composeapp.generated.resources.recommend_copied
import evoshift.composeapp.generated.resources.recommend_copy
import evoshift.composeapp.generated.resources.recommend_intro
import evoshift.composeapp.generated.resources.recommend_message
import evoshift.composeapp.generated.resources.recommend_message_label
import evoshift.composeapp.generated.resources.recommend_privacy
import evoshift.composeapp.generated.resources.recommend_share
import evoshift.composeapp.generated.resources.recommend_subject
import evoshift.composeapp.generated.resources.recommend_title
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource

/** Test tags of "Recommend EVoShift" (SH-239). */
object RecommendTags {
    const val MESSAGE = "recommend_message"
    const val SHARE = "recommend_share"
    const val COPY = "recommend_copy"
}

/**
 * SH-239: "Recommend EVoShift to another employer": a short message with the person's referral
 * link, which they may change, handed to the system share sheet ([share]; false where there is
 * none: desktop, older browsers) or copied.
 */
@Composable
fun RecommendScreen(
    viewModel: RecommendViewModel,
    onNavigateBack: () -> Unit,
    share: (text: String, subject: String) -> Boolean
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    RecommendContent(state, onNavigateBack, share, onRetry = viewModel::retry)
}

@Composable
fun RecommendContent(
    state: RecommendUiState,
    onNavigateBack: () -> Unit,
    share: (text: String, subject: String) -> Boolean,
    onRetry: () -> Unit
) {
    val snackbarHostState = remember { SnackbarHostState() }
    val scope = rememberCoroutineScope()
    val clipboard = LocalClipboard.current
    val copiedText = stringResource(Res.string.recommend_copied)
    val subject = stringResource(Res.string.recommend_subject)
    Scaffold(
        containerColor = Cream,
        topBar = { EvoTopBar(title = stringResource(Res.string.recommend_title), onBack = onNavigateBack) },
        snackbarHost = { SnackbarHost(snackbarHostState) }
    ) { padding ->
        Column(
            Modifier.fillMaxSize().padding(padding).verticalScroll(rememberScrollState())
                .contentWidth(FormMaxWidth).padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Text(stringResource(Res.string.recommend_intro), style = MaterialTheme.typography.bodyLarge, color = DeepNavy)
            val link = state.link
            when {
                link != null -> {
                    val initial = stringResource(Res.string.recommend_message, link)
                    var message by remember(initial) { mutableStateOf(initial) }
                    fun copy() {
                        scope.launch {
                            runCatching { clipboard.setClipEntry(plainTextClipEntry(message)) }
                            snackbarHostState.showSnackbar(copiedText)
                        }
                    }
                    RegularCard(Modifier.fillMaxWidth()) {
                        EvoTextField(
                            value = message,
                            onValueChange = { message = it },
                            label = stringResource(Res.string.recommend_message_label),
                            singleLine = false,
                            minLines = 5,
                            onLight = true,
                            modifier = Modifier.fillMaxWidth().padding(12.dp).testTag(RecommendTags.MESSAGE)
                        )
                    }
                    GoldButton(
                        text = stringResource(Res.string.recommend_share),
                        onClick = { if (!runCatching { share(message, subject) }.getOrDefault(false)) copy() },
                        modifier = Modifier.fillMaxWidth().testTag(RecommendTags.SHARE)
                    )
                    OutlinedButton(
                        onClick = ::copy,
                        modifier = Modifier.fillMaxWidth().handCursor().testTag(RecommendTags.COPY)
                    ) {
                        Icon(Icons.Default.ContentCopy, contentDescription = null, tint = DeepNavy)
                        Text(stringResource(Res.string.recommend_copy), color = DeepNavy, modifier = Modifier.padding(start = 8.dp))
                    }
                    Text(
                        stringResource(Res.string.recommend_privacy),
                        style = MaterialTheme.typography.bodySmall,
                        color = DeepNavy.copy(alpha = 0.7f)
                    )
                }
                state.isLoading -> Box(Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = DarkGold)
                }
                else -> {
                    state.error?.let { Text(it.asString(), color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodyMedium) }
                    OutlinedButton(onClick = onRetry, modifier = Modifier.handCursor()) {
                        Text(stringResource(Res.string.error_state_retry), color = DeepNavy)
                    }
                }
            }
        }
    }
}
